import React, { useState } from "react";
import { MediaProject } from "../../../../types/MediaProject";
import {
  createScheduledPost,
  SchedulePlatform,
} from "../../../../database/community/scheduledPosts";
import { useAsyncAction } from "../hooks/useAsyncAction";
import { SmallButton, Muted } from "./shared";

// Inline schedule control for a composed project. Queues a ScheduledPost that
// the MCP poller publishes at the due time. Extracted from
// the old ScheduleControls so the Library and the
// review queue share one implementation.

// "twitter" is the wire value Postproxy expects for X; there is no first-party
// X adapter, so those posts always route through Postproxy.
export const ALL_PLATFORMS: SchedulePlatform[] = [
  "instagram",
  "tiktok",
  "facebook",
  "linkedin",
  "twitter",
  "youtube",
];

export const platformLabel = (p: string): string =>
  p === "twitter" ? "X" : p === "youtube" ? "YouTube" : p;

const localDefault = (): string => {
  // datetime-local wants "YYYY-MM-DDTHH:mm" in local time; default +1h.
  const d = new Date(Date.now() + 60 * 60 * 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

export const SchedulePostDialog: React.FC<{
  communityId?: string;
  project: MediaProject;
  // First-party connection state per platform. Connected platforms publish via
  // our own app; the rest route via Postproxy.
  firstParty?: Partial<Record<SchedulePlatform, boolean>>;
  /** Queue as a draft that needs review approval instead of scheduling directly. */
  asDraft?: boolean;
  /** Label for the collapsed trigger button. */
  triggerLabel?: string;
  /** The authorized YouTube channel shown beside the upload controls. */
  youtubeChannelLabel?: string;
}> = ({
  communityId,
  project,
  firstParty,
  asDraft,
  triggerLabel,
  youtubeChannelLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [when, setWhen] = useState(localDefault());
  const [platforms, setPlatforms] = useState<SchedulePlatform[]>(["instagram"]);
  const [youtubePrivacy, setYoutubePrivacy] = useState<
    "private" | "unlisted" | "public"
  >("private");
  const [youtubeTitle, setYoutubeTitle] = useState(
    project.title || project.caption?.split(/\r?\n/).find(Boolean) || "Tribed video"
  );
  const [youtubeDescription, setYoutubeDescription] = useState(
    project.caption || ""
  );
  const action = useAsyncAction();

  const toggle = (p: SchedulePlatform) =>
    setPlatforms((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]
    );

  const handleQueue = () => {
    if (!communityId || !platforms.length) return;
    action.run(
      async () => {
        // datetime-local is local time; Date() parses it in local tz,
        // toISOString normalizes.
        const dueAt = new Date(when).toISOString();
        // One post per platform, so each can be reviewed, rescheduled, and
        // given its own image/caption independently.
        await Promise.all(
          platforms.map((platform) =>
            createScheduledPost(communityId, {
              platforms: [platform],
              caption:
                platform === "youtube" ? youtubeDescription : project.caption,
              projectId: project.id,
              dueAt,
              ...(asDraft ? { asDraft: true } : {}),
              ...(platform === "youtube"
                ? {
                    platformParams: {
                      youtube: {
                        title: youtubeTitle.trim(),
                        description: youtubeDescription,
                        privacyStatus: youtubePrivacy,
                        notifySubscribers: false,
                      },
                    },
                  }
                : {}),
            })
          )
        );
        setOpen(false);
        const n = platforms.length;
        const noun = n === 1 ? "post" : "posts";
        return asDraft
          ? `Queued ${n} ${noun} as drafts, one per platform. Approve them in Review.`
          : `Queued ${n} ${noun}, one per platform. The scheduler publishes each at the set time.`;
      },
      { fallbackError: "Could not queue the post." }
    );
  };

  if (!communityId) return null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        alignItems: "flex-end",
      }}
    >
      {!open ? (
        <SmallButton type="button" onClick={() => setOpen(true)}>
          {triggerLabel || "Schedule"}
        </SmallButton>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            alignItems: "flex-end",
            width: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 6,
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}
          >
            {ALL_PLATFORMS.map((p) => (
              <label
                key={p}
                title={
                  firstParty?.[p]
                    ? "Publishes via the connected account"
                    : "Publishes via the cross-post service"
                }
                style={{ display: "flex", alignItems: "center", gap: 3 }}
              >
                <input
                  type="checkbox"
                  checked={platforms.includes(p)}
                  onChange={() => toggle(p)}
                  disabled={p === "youtube" && project.kind !== "reel"}
                />
                <Muted>
                  {platformLabel(p)}
                  {firstParty?.[p] ? " ✓" : ""}
                  {p === "youtube" && project.kind !== "reel" ? " · video only" : ""}
                </Muted>
              </label>
            ))}
          </div>
          {platforms.includes("youtube") ? (
            <>
              <Muted>
                Upload to YouTube
                {youtubeChannelLabel ? ` · ${youtubeChannelLabel}` : ""}
              </Muted>
              <label style={{ width: "100%" }}>
                <Muted>YouTube title</Muted>
                <input
                  aria-label="Scheduled YouTube title"
                  value={youtubeTitle}
                  maxLength={100}
                  onChange={(e) => setYoutubeTitle(e.target.value)}
                  style={{ width: "100%" }}
                />
              </label>
              <label style={{ width: "100%" }}>
                <Muted>YouTube description</Muted>
                <textarea
                  aria-label="Scheduled YouTube description"
                  value={youtubeDescription}
                  maxLength={5000}
                  rows={3}
                  onChange={(e) => setYoutubeDescription(e.target.value)}
                  style={{ width: "100%", resize: "vertical" }}
                />
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Muted>YouTube visibility</Muted>
                <select
                  value={youtubePrivacy}
                  onChange={(e) =>
                    setYoutubePrivacy(
                      e.target.value as "private" | "unlisted" | "public"
                    )
                  }
                  style={{
                    fontFamily: "var(--font-sans)",
                    fontSize: 11,
                    padding: "3px 6px",
                    borderRadius: 6,
                    border: "1px solid var(--color-border-subtle)",
                    background: "var(--color-surface)",
                    color: "var(--color-text)",
                  }}
                >
                  <option value="private">Private</option>
                  <option value="unlisted">Unlisted</option>
                  <option value="public">Public</option>
                </select>
              </label>
            </>
          ) : null}
          <input
            type="datetime-local"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: 11,
              padding: "3px 6px",
              borderRadius: 6,
              border: "1px solid var(--color-border-subtle)",
              background: "var(--color-surface)",
              color: "var(--color-text)",
            }}
          />
          <div style={{ display: "flex", gap: 6 }}>
            <SmallButton
              type="button"
              onClick={() => setOpen(false)}
              disabled={action.busy}
            >
              Cancel
            </SmallButton>
            <SmallButton
              type="button"
              onClick={handleQueue}
              disabled={
                action.busy ||
                !platforms.length ||
                (platforms.includes("youtube") && !youtubeTitle.trim())
              }
            >
              {action.busy ? "Queuing…" : "Queue"}
            </SmallButton>
          </div>
        </div>
      )}
      {action.notice ? <Muted>{action.notice}</Muted> : null}
      {action.error ? (
        <Muted style={{ color: "var(--color-danger)" }}>{action.error}</Muted>
      ) : null}
    </div>
  );
};

export default SchedulePostDialog;
